import {Component, Inject, Input, OnInit, Optional, ViewChild, ViewEncapsulation} from '@angular/core';
import * as SharedModels from '@shared-models/index';
import {ImageService} from '@ui-app/services/image.service';
import {ICroppPhotoRequestOld, UPLOAD_URL} from '@shared-models/common.models';
import {FileUploadService} from '@ui-app/components/file-upload/file-upload.service';
import {HttpClient} from '@angular/common/http';
import {ToastrService} from 'ngx-toastr';
import {SpinnerService} from '@ui-app/components/spinner/spinner.service';
import {IPhotoReviewGridModelWithCheckbox} from '@employee-pages/photo-review/photo-review.component';
import {ApiConfig} from '@employee-configs/api.config';
import {filter, switchMap} from 'rxjs/operators';
import Cropper from 'cropperjs';
import {EPhotoReviewMode} from './cropper.models';
import {CustomModalService} from '@ui-app/services/custom-modal.service';
import {ConfirmModalComponent} from '@ui-app/modals/confirm-modal/confirm-modal.component';
import {AutomaticCroppingService} from '@ui-app/services/automatic-cropping.service';
import {BadgeRequestsService} from '../../core/services/data-access/badge-requests.service';
import {PhotoReviewModalBaseComponent} from './photo-review-modal-base.component';

@Component({
    selector: 'employee-photo-review-modal',
    templateUrl: './photo-review-modal.component.html',
    styleUrls: ['./photo-review-modal.component.less'],
    encapsulation: ViewEncapsulation.None
})
export class PhotoReviewModalComponent extends PhotoReviewModalBaseComponent implements OnInit {
    @Input() hideActionButtons = false; 
    @Input() showEditPhotoOption = false; 
    @Input() isFromProfile = false;
    @Input() onPhotoUpdated: () => void;
    @Input() onCancelEditing: () => void;

    constructor(
        http: HttpClient,
        imageService: ImageService,
        toasterService: ToastrService,
        spinnerService: SpinnerService,
        customModalService: CustomModalService,
        automaticCroppingService: AutomaticCroppingService,
        badgeRequestsService: BadgeRequestsService,
        @Optional() @Inject(UPLOAD_URL) uploadUrl: string,
        @Optional() @Inject(FileUploadService) fileUploadService: FileUploadService
    ) {
        super(
            http, imageService, toasterService, spinnerService, customModalService,
            automaticCroppingService, badgeRequestsService, uploadUrl, fileUploadService
        );
    }

    isApproveAndDeclineButton: boolean;
    isFurtherReviewButton: boolean;

    @Input() buttonsTitle = { dismiss: 'Close' };
    @Input() card: IPhotoReviewGridModelWithCheckbox;
    @ViewChild('image', { static: false }) imageElement;
    @Input() onApprovePhotoCallback = () => {};
    @Input() onDeclinePhotoCallback = () => {};
    @Input() furtherPhotoReviewCallback = () => {};
    ngOnInit(): void {
        const isValidPhotoSts = [
            SharedModels.CE.PhotoStatusEnum.FurtherReview,
            SharedModels.CE.PhotoStatusEnum.ApprovalPending
        ].includes(this.card.photoRequestStatusId);

        const isValidBadgeSts = [
            SharedModels.CE.BadgeStatusEnum.Pending,
            SharedModels.CE.BadgeStatusEnum.PendingPhotoCompliance
        ].includes(this.card.badgeRequestStatusId);

        const isValidFurtherReviewPhotoSts = this.card.photoRequestStatusId === SharedModels.CE.PhotoStatusEnum.ApprovalPending;

        const isSizePhotoCorrect = this.card.height >= this.minSizeOriginalPhoto.height
            && this.card.width >= this.minSizeOriginalPhoto.width;

        //this.isVisibleEditPhotoBtn = isValidPhotoSts && isValidBadgeSts && isSizePhotoCorrect;

        // Update isVisibleEditPhotoBtn to prioritize showEditPhotoOption
        this.isVisibleEditPhotoBtn = this.showEditPhotoOption || (isValidPhotoSts && isValidBadgeSts && isSizePhotoCorrect);
        this.isApproveAndDeclineButton = isValidPhotoSts && isValidBadgeSts;
        this.isFurtherReviewButton = isValidBadgeSts && isValidFurtherReviewPhotoSts;

        if (this.card.photoGuid) {
            this.subs = this.badgeRequestsService.getDownloadUrl(this.card.photoGuid).subscribe(photoGuid => {
                const elementId = 'ImageId_' + this.card.badgeRequestId;
                const element = document.getElementById(elementId);
                if (element) {
                    const srcValue = element.getAttribute('src') ?? '';
                    console.log(srcValue);
                    this.imageSource = srcValue;
                } else {
                    this.imageSource = photoGuid.url;
                }
            });
        }

        if (this.card.originalPhotoGuid) {
            this.subs = this.badgeRequestsService.getDownloadUrl(this.card.originalPhotoGuid)
                .subscribe((originalPhotoGuid) => {
                this.originalPhotoGuidUrl = originalPhotoGuid.url;
            });
        }
        this.subs = this.automaticCroppingService.getSetting().subscribe((settings) => {
            this.isImageRecognitionEnabled = settings.isImageRecognitionEnabled;
        });
    }

    onPhotoReview(): void {
        this.spinnerService.next(true);
        this.initCropperSettings(this.card.croppInfo);
        this.togglePhotoReviewModalMode();
    }

    savePhoto(): void {
        const request = {
            badgeRequestId: this.card.badgeRequestId,
            croppInfo: JSON.stringify(this.cropperData),
            isAutocropped: false,
            uId: null
        };
        this.card.croppInfo = request.croppInfo;

       const dataUrl= this.cropper.getCroppedCanvas().toDataURL('image/png');
       this.imageSource=dataUrl

        this.subs = this.savePhotoFirstStep(request, dataUrl)
        .subscribe(__ => {
                const element = document.getElementById('ImageId_'+this.card.badgeRequestId);
                    if (element) {
                        element.setAttribute('src', dataUrl);
                    }
                 this.toasterService.success('Photo was cropped');

                 if (this.onPhotoUpdated) {
                    this.onPhotoUpdated(); // Call the parent callback
                }
                this.action(true);

            }, __ => {
                this.toasterService.error('Photo can\'t be changed. Please refresh the page and try again');
                this.action(true);
            });
    }

    onResetToOriginal(): void {
        this.subs = this.customModalService.initModal(ConfirmModalComponent, {
            description: 'Results of cropping will be deleted. Are you sure you want to reset photo to original?'
        })
            .pipe(filter((isConfirmed: boolean) => isConfirmed))
            .subscribe(() => this.resetCroppPhoto());
    }

    resetCroppPhoto(): void {
        const request = {
            badgeRequestId: this.card.badgeRequestId,
            uId: this.card.originalPhotoGuid,
            croppInfo: null
        };
        this.card.croppInfo = null;
    
        this.subs = this.http.post(`${ApiConfig.ImageRekognition.croppPhoto}`, request).pipe(
            switchMap(_ => {
                // Call the second API after the first one completes
                return this.badgeRequestsService.getDownloadUrl(this.card.photoGuid);
            }),
    
        ).subscribe(
            photoGuid => {
                if (photoGuid) {
                    this.imageSource = photoGuid.url;
                    const element = document.getElementById('ImageId_' + this.card.badgeRequestId);
                    if (element) {
                        element.setAttribute('src', this.imageSource);
                    }
                    this.toasterService.success('Photo was reset to original');
                }
                if (this.onPhotoUpdated) {
                    this.onPhotoUpdated(); // Call the parent callback
                }
                this.action(true);
            },
            error => {
                this.toasterService.error('Photo can\'t be changed. Please refresh the page and try again');
                this.action(true);
            }
        );
    }

    action(close: boolean): void {
        if (close) { // Check if the modal should be closed
            if (this.onCancelEditing) { // Check if the parent provided an onCancelEditing callback
                this.onCancelEditing(); // Notify the parent component to close the modal
            } else {
                this.customModalService.closeModal(PhotoReviewModalComponent); // Fallback: Close the modal directly
            }
        }
    }
}

function of(arg0: null) {
    throw new Error('Function not implemented.');
}

